import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Pause, SkipForward, SkipBack, Volume2, List, RefreshCw, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const SpotifyPlayer = ({ sessionPlaylist = [], onSongChange }) => {
  const [player, setPlayer] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [sdkError, setSdkError] = useState(null);
  const [queueSynced, setQueueSynced] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [currentQueueIndex, setCurrentQueueIndex] = useState(0);
  const progressInterval = useRef(null);
  const lastPlaylistRef = useRef('');
  const playerRef = useRef(null);

  // Load the Spotify Web Playback SDK
  useEffect(() => {
    if (document.getElementById('spotify-sdk-script')) return;

    window.onSpotifyWebPlaybackSDKReady = () => {
      console.log('[SpotifyPlayer] SDK ready');
      initializePlayer();
    };

    const script = document.createElement('script');
    script.id = 'spotify-sdk-script';
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    script.onerror = () => {
      setSdkError('Failed to load Spotify Playback SDK');
    };
    document.head.appendChild(script);

    // If SDK was already loaded
    if (window.Spotify) {
      initializePlayer();
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
      }
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAccessToken = useCallback(async () => {
    // Fetch a fresh token from the server (server reads from Firebase)
    try {
      const { default: api } = await import('../utils/api');
      const response = await api.get('/api/auth/spotify/status');
      if (response.data.connected) {
        // The actual token is stored server-side; for the SDK we need it client-side.
        // We'll fetch it via a dedicated endpoint.
        const tokenResp = await api.get('/api/auth/spotify/player-token');
        return tokenResp.data.access_token;
      }
    } catch (err) {
      console.error('[SpotifyPlayer] Failed to get access token:', err);
    }
    return null;
  }, []);

  const initializePlayer = useCallback(async () => {
    if (!window.Spotify || playerRef.current) return;

    const token = await getAccessToken();
    if (!token) {
      setSdkError('No Spotify access token available. Please reconnect Spotify.');
      return;
    }

    const spotifyPlayer = new window.Spotify.Player({
      name: 'JukeHive Player',
      getOAuthToken: async (cb) => {
        // Always get a fresh token in case it was refreshed
        const freshToken = await getAccessToken();
        cb(freshToken || token);
      },
      volume: 0.5
    });

    // Error handlers
    spotifyPlayer.addListener('initialization_error', ({ message }) => {
      console.error('[SpotifyPlayer] Init error:', message);
      setSdkError(`Initialization error: ${message}`);
    });

    spotifyPlayer.addListener('authentication_error', ({ message }) => {
      console.error('[SpotifyPlayer] Auth error:', message);
      setSdkError('Spotify authentication error. Please reconnect.');
    });

    spotifyPlayer.addListener('account_error', ({ message }) => {
      console.error('[SpotifyPlayer] Account error:', message);
      setSdkError('Spotify Premium is required for playback.');
    });

    spotifyPlayer.addListener('playback_error', ({ message }) => {
      console.error('[SpotifyPlayer] Playback error:', message);
    });

    // Playback status updates
    spotifyPlayer.addListener('player_state_changed', (state) => {
      if (!state) return;

      const track = state.track_window.current_track;
      if (track) {
        const trackInfo = {
          id: track.id,
          name: track.name,
          artist: track.artists.map(a => a.name).join(', '),
          album: track.album.name,
          image: track.album.images[0]?.url,
          uri: track.uri
        };
        setCurrentTrack(trackInfo);
        if (onSongChange) {
          onSongChange(trackInfo);
        }
      }

      setIsPlaying(!state.paused);
      setCurrentTime(state.position);
      setDuration(state.duration);

      // Track queue position
      const { next_tracks } = state.track_window;
      const totalTracks = sessionPlaylist.filter(s => s.musicService === 'spotify').length;
      if (totalTracks > 0) {
        setCurrentQueueIndex(totalTracks - next_tracks.length - 1);
      }
    });

    // Ready
    spotifyPlayer.addListener('ready', ({ device_id }) => {
      console.log('[SpotifyPlayer] Ready with device ID:', device_id);
      setDeviceId(device_id);
      setIsReady(true);
      setSdkError(null);
    });

    // Not ready
    spotifyPlayer.addListener('not_ready', ({ device_id }) => {
      console.log('[SpotifyPlayer] Device went offline:', device_id);
      setIsReady(false);
    });

    await spotifyPlayer.connect();
    playerRef.current = spotifyPlayer;
    setPlayer(spotifyPlayer);
  }, [getAccessToken, onSongChange, sessionPlaylist]);

  // Progress timer
  useEffect(() => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    if (isPlaying && player) {
      progressInterval.current = setInterval(() => {
        player.getCurrentState().then((state) => {
          if (state) {
            setCurrentTime(state.position);
            setDuration(state.duration);
          }
        });
      }, 250);
    }

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [isPlaying, player]);

  // Sync queue when playlist changes
  const syncQueue = useCallback(async () => {
    if (!deviceId || !isReady || isSyncing) return;

    const spotifySongs = sessionPlaylist.filter(s => s.musicService === 'spotify' && s.uri);
    if (spotifySongs.length === 0) return;

    // Check if playlist actually changed
    const playlistKey = spotifySongs.map(s => s.uri).join(',');
    if (playlistKey === lastPlaylistRef.current) {
      setQueueSynced(true);
      return;
    }

    try {
      setIsSyncing(true);
      setQueueSynced(false);

      const token = await getAccessToken();
      if (!token) {
        setSdkError('No access token for playback');
        return;
      }

      // Use the Spotify Web API to start playback with the track URIs
      const uris = spotifySongs.map(s => s.uri);

      const response = await fetch('https://api.spotify.com/v1/me/player/play', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          device_id: deviceId,
          uris
        })
      });

      if (response.status === 204 || response.ok) {
        lastPlaylistRef.current = playlistKey;
        setQueueSynced(true);
        console.log(`[SpotifyPlayer] Synced ${uris.length} tracks`);
      } else {
        const errData = await response.json().catch(() => ({}));
        console.error('[SpotifyPlayer] Play request failed:', errData);
        toast.error('Failed to sync playlist with Spotify');
      }
    } catch (err) {
      console.error('[SpotifyPlayer] Sync error:', err);
      toast.error('Failed to sync playlist: ' + err.message);
    } finally {
      setIsSyncing(false);
    }
  }, [deviceId, isReady, isSyncing, sessionPlaylist, getAccessToken]);

  // Auto-sync when playlist changes
  useEffect(() => {
    if (sessionPlaylist.length > 0 && isReady && !isSyncing && !queueSynced) {
      const timeoutId = setTimeout(() => {
        syncQueue();
      }, 1500);
      return () => clearTimeout(timeoutId);
    }
  }, [sessionPlaylist.length, isReady, isSyncing, queueSynced, syncQueue]);

  // Reset sync state when playlist changes
  useEffect(() => {
    const spotifySongs = sessionPlaylist.filter(s => s.musicService === 'spotify' && s.uri);
    const playlistKey = spotifySongs.map(s => s.uri).join(',');
    if (playlistKey !== lastPlaylistRef.current) {
      setQueueSynced(false);
    }
  }, [sessionPlaylist]);

  const togglePlayPause = async () => {
    if (!player) return;
    try {
      await player.togglePlay();
    } catch (err) {
      console.error('[SpotifyPlayer] Toggle error:', err);
    }
  };

  const skipToNext = async () => {
    if (!player) return;
    try {
      await player.nextTrack();
    } catch (err) {
      console.error('[SpotifyPlayer] Skip next error:', err);
    }
  };

  const skipToPrevious = async () => {
    if (!player) return;
    try {
      await player.previousTrack();
    } catch (err) {
      console.error('[SpotifyPlayer] Skip prev error:', err);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (player) {
      player.setVolume(newVolume);
    }
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (sdkError) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 border border-red-500/30">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">Spotify Player Error</h3>
            <p className="text-red-300 text-sm">{sdkError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isReady) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-600">
        <h3 className="text-xl font-semibold text-white mb-4">Spotify Player</h3>
        <div className="flex items-center justify-center py-8 text-gray-400">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500 mr-3"></div>
          <span>Initializing Spotify player...</span>
        </div>
      </div>
    );
  }

  const spotifySongCount = sessionPlaylist.filter(s => s.musicService === 'spotify').length;

  return (
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-600">
      <h3 className="text-xl font-semibold text-white mb-4">Now Playing</h3>

      {currentTrack || spotifySongCount > 0 ? (
        <div className="space-y-4">
          {/* Track Info */}
          {currentTrack && (
            <div className="flex items-center space-x-4">
              {currentTrack.image && (
                <img
                  src={currentTrack.image}
                  alt={currentTrack.name}
                  className="w-16 h-16 rounded-lg"
                />
              )}
              <div className="flex-1">
                <h4 className="font-semibold text-white">{currentTrack.name}</h4>
                <p className="text-gray-400">{currentTrack.artist}</p>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="bg-gray-600 rounded-full h-3 overflow-hidden">
              <div
                className="bg-green-500 h-full transition-all duration-100"
                style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
              />
            </div>
            <div className="flex justify-between text-sm text-gray-400 font-mono">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={skipToPrevious}
              className="text-gray-400 hover:text-white active:text-green-400 transition-colors p-2 -m-2"
              aria-label="Previous track"
            >
              <SkipBack className="w-6 h-6" />
            </button>

            <button
              onClick={togglePlayPause}
              disabled={isSyncing}
              className={`${
                isSyncing
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 active:bg-green-800'
              } text-white rounded-full p-4 transition-all transform ${!isSyncing ? 'active:scale-95' : ''}`}
              aria-label={isSyncing ? 'Syncing...' : (isPlaying ? 'Pause' : 'Play')}
            >
              {isSyncing ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-6 h-6" />
              ) : (
                <Play className="w-6 h-6 ml-0.5" />
              )}
            </button>

            <button
              onClick={skipToNext}
              className="text-gray-400 hover:text-white active:text-green-400 transition-colors p-2 -m-2"
              aria-label="Next track"
            >
              <SkipForward className="w-6 h-6" />
            </button>
          </div>

          {/* Volume */}
          <div className="flex items-center space-x-3">
            <Volume2 className="w-4 h-4 text-gray-400" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className="flex-1 accent-green-600"
            />
          </div>

          {/* Queue Status */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-600">
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <List className="w-4 h-4" />
              <span>
                {spotifySongCount > 0
                  ? `${currentQueueIndex + 1} of ${spotifySongCount} Spotify songs`
                  : 'No Spotify songs in queue'}
              </span>
            </div>
            {spotifySongCount > 0 && (
              <button
                onClick={syncQueue}
                className="text-gray-400 hover:text-white transition-colors flex items-center space-x-1 text-sm"
                title="Sync queue with session playlist"
              >
                <RefreshCw className={`w-4 h-4 ${!queueSynced ? 'animate-spin' : ''}`} />
                <span>{queueSynced ? 'Synced' : 'Sync'}</span>
              </button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-gray-400 text-center py-8">
          No songs in playlist. Add some songs to start playing!
        </p>
      )}
    </div>
  );
};

export default SpotifyPlayer;
